import ArrowBtn from "@/assets/ArrowBtn";
import { Property, Survey } from "@/features/survey/types";
import { hasAddressLine2AsCustom } from "@/shared/utils";
import { getIconData } from "@/shared/utils/map";
import { isFieldVisible } from "@/shared/utils/propertyFieldRules";
import { getPropertyOrderWithoutCurrentLocation } from "@/shared/utils/propertyUtils";
import { EmeraldIcon } from "@emerald-react/icon";
import { useNavigate } from "react-router";

interface PropertyHeaderProps {
  property: Property;
  propertyId: string;
  currentSurvey: Survey;
  currentPropertyIndex: number;
  propertyList: Property[];
}

const PropertyHeader = ({
  property,
  propertyId,
  currentSurvey,
  currentPropertyIndex,
  propertyList,
}: PropertyHeaderProps) => {
  const navigate = useNavigate();

  const iconColor = getIconData(property).iconColor;
  // Get the property order number for display
  const getPropertyOrderIcon = () => {
    if (property?.isCurrentLocation) {
      return (
        <div
          className="mt-2 flex size-6 items-center justify-center rounded-full bg-white shadow-xs"
          style={{ color: iconColor }}
        >
          <EmeraldIcon icon="stars" type="filled" variant="icon" />
        </div>
      );
    }
    const orderIndex = getPropertyOrderWithoutCurrentLocation(
      propertyId,
      propertyList,
    );
    return orderIndex !== -1 ? orderIndex + 1 : "?";
  };

  // Navigation functions
  const previousProperty = () => {
    if (currentPropertyIndex > 0) {
      const prevProperty = propertyList[currentPropertyIndex - 1];
      navigate(`/interactive/${currentSurvey?.id}/property/${prevProperty.id}`);
    }
  };

  const nextProperty = () => {
    if (currentPropertyIndex < propertyList.length - 1) {
      const nextProperty = propertyList[currentPropertyIndex + 1];
      navigate(`/interactive/${currentSurvey?.id}/property/${nextProperty.id}`);
    }
  };

  // Determine if name/address1 fields are toggled on in field rules.
  // Default to true when no field rules are configured (fallback-safe).
  const hasFieldRules =
    !!currentSurvey?.surveyPropertyFieldRule?.propertyFieldRuleList?.length;
  const nameFieldVisible = hasFieldRules
    ? isFieldVisible(currentSurvey, "name")
    : true;
  const address1FieldVisible = hasFieldRules
    ? isFieldVisible(currentSurvey, "address1")
    : true;
  const cityFieldVisible = hasFieldRules
    ? isFieldVisible(currentSurvey, "city")
    : true;
  const stateFieldVisible = hasFieldRules
    ? isFieldVisible(currentSurvey, "stateProvince")
    : true;
  const postalCodeFieldVisible = hasFieldRules
    ? isFieldVisible(currentSurvey, "postalCode")
    : true;
  const address2FieldVisible = hasFieldRules
    ? isFieldVisible(currentSurvey, "address2") ||
      isFieldVisible(currentSurvey, "Address Line 2")
    : true;
  const hasAddressLine2Custom = hasAddressLine2AsCustom(currentSurvey);

  // Title: use property name when the field is visible AND name has real data;
  // otherwise fall back to address1 (always shown at least once)
  const propertyNameValue = property?.name || property?.propertyName;
  const hasRealName =
    !!propertyNameValue &&
    propertyNameValue.trim() !== "" &&
    propertyNameValue !== "Untitled Property";
  const nameIsTitle = nameFieldVisible && hasRealName;

  const displayTitle = nameIsTitle
    ? propertyNameValue!
    : property?.address1 || "Property";

  // Address line: when name is title include address1 (if visible) + city/state/zip;
  // when address1 is already the title show only city/state/zip to avoid duplication
  const formatAddress = (): string => {
    const stateZip = [
      stateFieldVisible ? property?.stateProvince : null,
      postalCodeFieldVisible ? property?.postalCode : null,
    ]
      .filter(Boolean)
      .join("  ");
    const cityStateZip = [
      cityFieldVisible ? property?.city : null,
      stateZip || null,
    ].filter(Boolean);

    if (nameIsTitle) {
      const parts = [
        address1FieldVisible ? property?.address1?.replace(/,+\s*$/, "") : null,
        ...cityStateZip,
      ].filter(Boolean);
      return parts.join(", ");
    }

    // address1 is already the title — omit it from the address line
    return cityStateZip.join(", ");
  };

  const formatAddress2WithCityStateZip = (): string => {
    const stateZip = [
      stateFieldVisible ? property?.stateProvince : null,
      postalCodeFieldVisible ? property?.postalCode : null,
    ]
      .filter(Boolean)
      .join("  ");
    const address2 = property?.address2?.replace(/,+\s*$/, "");
    const parts = [
      address2 === "—" ? "" : address2,
      cityFieldVisible ? property?.city : null,
      stateZip || null,
    ].filter(Boolean);

    return parts.join(", ");
  };

  const addressLine = formatAddress();
  const shouldUseAddress2Layout =
    !hasAddressLine2Custom && address2FieldVisible;

  return (
    <div className="property-header">
      <div className="flex items-center justify-between w-full">
        {/* Left Section: Property Order Icon + Title */}
        <div className="flex items-center flex-1">
          {/* Property Order */}
          <div className="flex items-center mr-2 lg:mr-3">
            <div className="text-xl lg:text-2xl text-gray-500 mr-2 lg:mr-3">
              {getPropertyOrderIcon()}
            </div>
            <div
              className="border-l mx-1 h-12 lg:h-16"
              style={{ borderColor: "var(--color-secondary)" }}
            />
          </div>

          {/* Property Title and Address */}
          <div className="flex-1 flex flex-col gap-y-1">
            <div className="text-lg lg:text-3xl leading-9 fancy text-primary">
              {displayTitle}
            </div>
            {shouldUseAddress2Layout ? (
              <div className="text-xs lg:text-sm text-primary flex flex-col">
                {nameIsTitle && address1FieldVisible && property?.address1 && (
                  <div>{property.address1.replace(/,+\s*$/, "")}</div>
                )}
                <div>{formatAddress2WithCityStateZip()}</div>
              </div>
            ) : (
              addressLine && (
                <div className="text-xs lg:text-sm text-primary">
                  {addressLine}
                </div>
              )
            )}
          </div>
        </div>

        {/* Right Section: Navigation */}
        {currentSurvey?.properties && (
          <div className="flex items-center space-x-1 lg:space-x-2">
            {/* Previous Button */}
            <ArrowBtn
              stroke="#003F2D"
              strokeWidth={2}
              onClick={previousProperty}
              disabled={currentPropertyIndex === 0}
            />
            {/* Property Counter */}
            <span className="text-xs lg:text-sm text-gray-600 px-2 lg:px-3">
              <span className="hidden sm:inline">
                {currentPropertyIndex + 1} of {propertyList.length}
              </span>
              <span className="sm:hidden">
                {currentPropertyIndex + 1}/{propertyList.length}
              </span>
            </span>

            {/* Next Button */}
            <ArrowBtn
              stroke="#003F2D"
              strokeWidth={2}
              direction="right"
              onClick={nextProperty}
              disabled={currentPropertyIndex === propertyList.length - 1}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default PropertyHeader;
